import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Task : famille "unité de travail interne".
 *
 * Tâche assignable et datée, ponctuelle ou récurrente, rattachée ou non à un
 * projet, un ticket ou une fiche contact, suivie par statut jusqu'à son
 * achèvement. Construite sur le modèle "famille + type" de la bible (patron :
 * `asset`).
 *
 * Le socle ne connaît pas la nature métier concrète ("rappel", "intervention",
 * "relance") : il connaît la famille générique `task`. Le vocabulaire métier
 * est porté par le champ `type` (chaîne libre déclarée par l'app) ; l'avancement
 * (`status`) et l'urgence (`priority`) sont des états métier à part, chaînes
 * libres pilotées par l'app.
 *
 * Références vers d'autres familles = de simples ids (aucun import de schéma) :
 * `assigneeWorkerId` (worker en charge), les parents `projectId` / `ticketId` /
 * `contactId`, et les liens `workOrderId` / `messageId`. Toutes optionnelles :
 * une tâche peut n'être rattachée à rien. La récurrence est décrite par une
 * règle textuelle (`recurrenceRule`, ex. format iCal RRULE).
 *
 * Accès : v0 reste org-wide (même doctrine que asset). Rien de sensible signalé
 * sur cette famille.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille task (hors champs standard du socle). */
export const taskCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "rappel", "intervention"). */
  type: z.string().min(1),
  /** Worker en charge (référence `worker`), optionnel : une tâche peut être non assignée. */
  assigneeWorkerId: z.string().min(1).optional(),
  /** Avancement, chaîne libre pilotée par l'app (ex. "a_faire", "en_cours", "termine"). Optionnel. */
  status: z.string().min(1).optional(),
  /** Urgence, chaîne libre pilotée par l'app (ex. "basse", "normale", "haute", "urgente"). Optionnel. */
  priority: z.string().min(1).optional(),
  /** Échéance de la tâche. Optionnel. */
  dueAt: z.date().optional(),
  /** Règle de récurrence textuelle (ex. format iCal RRULE) si la tâche se répète. Optionnel. */
  recurrenceRule: z.string().min(1).optional(),
  /** Projet parent de rattachement (référence `project`). Optionnel. */
  projectId: z.string().min(1).optional(),
  /** Ticket parent de rattachement (référence `ticket`). Optionnel. */
  ticketId: z.string().min(1).optional(),
  /** Fiche contact parent de rattachement (référence `contact`). Optionnel. */
  contactId: z.string().min(1).optional(),
  /** Ordre de travail lié (référence `work-order`). Optionnel. */
  workOrderId: z.string().min(1).optional(),
  /** Message lié (référence `message`). Optionnel. */
  messageId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille task (champs standard + champs métier). */
export const TaskSchema = z.object({
  ...baseEntityFields,
  ...taskCoreFields,
});

/** Type d'une task. */
export type Task = z.infer<typeof TaskSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const TaskCreateInputSchema = z.object({
  ...taskCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'une task. */
export type TaskCreateInput = z.infer<typeof TaskCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const TaskUpdateInputSchema = z
  .object({ ...taskCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'une task. */
export type TaskUpdateInput = z.infer<typeof TaskUpdateInputSchema>;
